import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, StateKey, TransferState, makeStateKey } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { WINDOW } from 'src/app/shared/services/window/window.service';
import { environment } from 'src/environments/environment';
import { ClientService } from '../../services/client/client.service';
import { SharedService } from '../../services/shared-service/shared.service';
import { StorageService } from '../../services/storage/storage.service';
import { incomingLanguages, languageCodes } from '../../constants';

const TranslationsKey = makeStateKey('translations') as StateKey<Object>;

@Injectable({
  providedIn: 'root',
})
export class TranslateService {
  private path: string = `${environment.apiUrl}/translations/language/{{lang}}`;
  private error: boolean = false;
  private data!: Object;
  private fallbackData!: Object;
  private translationsLoaded$ = new BehaviorSubject<boolean>(false);
 
  private fallbackLanguage = this.Client.fallbackLanguage;
  private fallbackCurrency = 'EUR';

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(WINDOW) public window: Window,
    private http: HttpClient,
    private Storage: StorageService,
    private Client: ClientService,
    private Shared: SharedService,
    private tstate: TransferState,
  ) {}

  private _language: string = '';
  private _currency = '';

  public get currency() {
    if (this._currency === '') {
      let tmp = this.Storage.get('selectedCurrency');
      if (!tmp) {
        tmp = this.Client.client.currency;
      }
      if (!tmp) {
        tmp = this.fallbackCurrency;
      }
      this._currency = tmp;
    }
    return this._currency;
  }

  public get language() {
    if (this._language === '') {
      this._language = this.findActiveLanguage();
    }
    return this._language;
  }

  private findActiveLanguage() {
    const urlLangPart = this.document.location.pathname.split('/')[1];

    // get the language from url
    let currentLang = urlLangPart?.length === 2 ? urlLangPart : undefined;
    if (currentLang) {
      let _foundLang = 'en';
      incomingLanguages.forEach(language => {
        if(currentLang === language.incoming || currentLang === language.lang){
          _foundLang = language.lang;
        }
      });
      currentLang = _foundLang;
      this.Storage.set('selectedLanguage', currentLang);
    }

    // if missing then get the language from cookies
    if (!currentLang) {
      currentLang = this.Storage.get('selectedLanguage');
    }

    //if missing then get the client default language
    if (!currentLang) {
      currentLang = this.Client.client?.defaultLanguage;
    }

    // if missing then get fallback language from listOfLanguages
    // if fallback language doesnt exist in list, get the first language from list
    if (!currentLang) {
      const langList = this.Client?.client?.listOfLanguages;
      if (langList?.indexOf(this.fallbackLanguage) !== -1) {
        currentLang = this.fallbackLanguage;
      } else {
        currentLang = this.Client?.client?.listOfLanguages[0];
        console.log(
          `Client doesn't have '${this.fallbackLanguage}' language enabled. Using first language in 'listOfLanguages': '${currentLang}' selected`
        );
      }
    }
    return currentLang;
  }

  public updateCurrentSelectedCurrency(currency: string) {
    this.Storage.set('selectedCurrency', currency);
    if (this.Shared.isBrowser()) {
      this.window.location.reload();
    }
  }

  findObj(obj: any, path: any) {
    let output;
    try {
      output = obj[path]
        ? obj[path]
        : path
            .replace(/\[|\]\.?/g, '.')
            .split('.')
            .filter((s: any) => s)
            .reduce((acc: any, val: any) => acc && acc[val], obj);
    } catch (err) {
      output = undefined;
    }
    return output;
  }

  get(key: string = '', replace?: any, part?: number) {
    if (!key) {
      return '';
    }
    let value = '';
    if (!this.data) {
      value = `${key}`;
    } else {
      value = this.findObj(this.data, key);
      if (!value && this.fallbackData) {
        value = this.findObj(this.fallbackData, key);
      }
      if (!value) {
        value = key;
      }
    }
    
    if (replace) {
      Object.keys(replace).forEach((key) => {
        const rex = new RegExp(`{{\s{0,40}${key}\s{0,40}}}`, 'img');
        value = value.replace(rex, replace[key]);
      });
    }
    return typeof part === 'number' ? value.split(/<[^>]+>/gim)[part] : value;
  }

  load() {
    return new Promise(async (resolve, reject) => {
      if (this.language !== 'en') {
        const tmp = await firstValueFrom(
          this.http.get(this.path.replace('{{lang}}', 'en'))
        );
        this.fallbackData = tmp;
      }
      const httpResponse = (response: any) => {
        this.tstate.set(TranslationsKey, response);
        this.data = response;
        this.translationsLoaded$.next(true);
        resolve(true);
      };
      const errorHandler = () => resolve(true);
      const path = this.path.replace('{{lang}}', this.language);
      this.http.get(path).subscribe({
        next: httpResponse,
        error: errorHandler,
      });
    });
  }
}
